import React from 'react'
import { Box, Button } from '@mui/material'
import { CheckCircle as ApproveIcon, Cancel as RejectIcon } from '@mui/icons-material'
import ProposalDetailBase from '../base/ProposalDetailBase'
import CategorizedEventChangesTable from '@/components/proposals/CategorizedEventChangesTable'
import CategorizedEditionChangesTable from '@/components/proposals/CategorizedEditionChangesTable'
import RacesChangesTable from '@/components/proposals/edition-update/RacesChangesTable'
import OrganizerSection from '@/components/proposals/edition-update/OrganizerSection'
import ProposalJustificationsCard from '@/components/proposals/ProposalJustificationsCard'
import AgentCard from '@/components/proposals/AgentCard'
import EditionContextInfo from '@/components/proposals/EditionContextInfo'
import { RejectedMatchesCard } from '@/components/proposals/new-event/RejectedMatchesCard'
import { ExistingEventAlert } from '@/components/proposals/new-event/ExistingEventAlert'
import { useCheckExistingEvent } from '@/hooks/useApi'
import { isFieldOfEntityType } from '@/constants/fieldCategories'

interface NewEventDetailProps {
  proposalId: string
}

const NewEventDetail: React.FC<NewEventDetailProps> = ({ proposalId }) => {
  // Vérifier si un événement correspondant existe maintenant dans Miles Republic
  // Le hook gère le cas où la proposition n'est pas NEW_EVENT ou PENDING (erreur 400 ignorée)
  const { data: checkExistingResult } = useCheckExistingEvent(proposalId, true)

  return (
    <ProposalDetailBase
      proposalId={proposalId}
      renderMainContent={(context) => {
        const {
          consolidatedChanges,
          consolidatedRaceChanges,
          selectedChanges,
          userModifiedRaceChanges,
          handleFieldSelect,
          handleFieldModify,
          handleEditionStartDateChange,
          handleApproveAll,
          handleRejectAll,
          handleRaceFieldModify,
          userModifiedChanges,
          formatValue,
          formatAgentsList,
          editionTimezone,
          allPending,
          isPending,
          isEventDead,
          isEditionCanceled,
          isReadOnly, // ⚠️ PHASE 3: Flag lecture seule
          proposal,
          // Validation par blocs
          validateBlock,
          unvalidateBlock,
          isBlockValidated,
          isBlockPending,
          blockProposals
        } = context

        // Extraire l'année de l'édition pour l'alerte
        const editionYear = proposal.changes?.edition?.new?.year
          ? parseInt(proposal.changes.edition.new.year)
          : proposal.editionYear || new Date().getFullYear()

        // Classification des champs via fieldCategories (source de vérité unique).
        // Event = champs catalogués EVENT ; Organisateur = champ 'organizer' ;
        // Édition = catch-all (tout le reste hors event/organizer/courses).
        // Les champs vides éditables (URLs, etc.) sont désormais injectés en amont par le hook.
        const isRaceField = (f: string) =>
          f.startsWith('races') || ['racesToUpdate', 'racesToAdd', 'racesExisting', 'racesToDelete', 'racesToCreate'].includes(f)

        const organizerChange = consolidatedChanges.find(c => c.field === 'organizer')
        const eventChanges = consolidatedChanges.filter(c => isFieldOfEntityType(c.field, 'EVENT'))
        const editionChanges = consolidatedChanges.filter(c =>
          !isFieldOfEntityType(c.field, 'EVENT') && c.field !== 'organizer' && !isRaceField(c.field)
        )

        return (
          <>
            {/* Alerte si un événement correspondant existe maintenant */}
            {checkExistingResult?.hasMatch && (checkExistingResult.match || checkExistingResult.matches) && (
              <ExistingEventAlert
                proposalId={proposal.id}
                match={checkExistingResult.match || checkExistingResult.matches![0]}
                matches={checkExistingResult.matches}
                proposalYear={editionYear}
              />
            )}

            <CategorizedEventChangesTable
              title="Informations de l'événement"
              changes={eventChanges}
              isNewEvent={true}
              selectedChanges={selectedChanges}
              onFieldSelect={handleFieldSelect}
              onFieldModify={handleFieldModify}
              userModifiedChanges={userModifiedChanges}
              formatValue={formatValue}
              formatAgentsList={formatAgentsList}
              timezone={editionTimezone}
              disabled={isReadOnly || !allPending || isPending || isEventDead}
              isBlockValidated={isBlockValidated('event')}
              onValidateBlock={isReadOnly ? undefined : () => validateBlock('event')}
              onUnvalidateBlock={isReadOnly ? undefined : () => unvalidateBlock('event')}
              isBlockPending={isBlockPending}
              showCurrentValue={false}
              showConfidence={false}
              showActions={false}
            />

            <CategorizedEditionChangesTable
              title="Informations de l'édition"
              changes={editionChanges}
              isNewEvent={true}
              selectedChanges={selectedChanges}
              onFieldSelect={handleFieldSelect}
              onFieldModify={handleFieldModify}
              onEditionStartDateChange={handleEditionStartDateChange}
              userModifiedChanges={userModifiedChanges}
              formatValue={formatValue}
              formatAgentsList={formatAgentsList}
              timezone={editionTimezone}
              disabled={isReadOnly || !allPending || isPending || isEventDead}
              isEditionCanceled={isEditionCanceled || isEventDead}
              isBlockValidated={isBlockValidated('edition')}
              onValidateBlock={isReadOnly ? undefined : () => validateBlock('edition')}
              onUnvalidateBlock={isReadOnly ? undefined : () => unvalidateBlock('edition')}
              isBlockPending={isBlockPending}
              showCurrentValue={false}
              showConfidence={false}
              showActions={false}
              // actions - ❌ OBSOLETE : Boutons "Tout approuver" / "Tout rejeter" remplacés par validation par blocs
            />

            {organizerChange && (
              <OrganizerSection
                change={organizerChange}
                onApprove={() => {/* Single proposal - no field-specific approve */}}
                onFieldModify={handleFieldModify}
                userModifiedChanges={userModifiedChanges}
                disabled={isReadOnly || isBlockValidated('organizer') || isEventDead}
                isBlockValidated={isBlockValidated('organizer')}
                onValidateBlock={isReadOnly ? undefined : () => validateBlock('organizer')}
                onUnvalidateBlock={isReadOnly ? undefined : () => unvalidateBlock('organizer')}
                isBlockPending={isBlockPending}
                validationDisabled={isReadOnly || isEventDead}
                showCurrentValue={false}
                showConfidence={false}
                showActions={false}
              />
            )}

            <RacesChangesTable
              consolidatedRaces={consolidatedRaceChanges}
              userModifiedRaceChanges={userModifiedRaceChanges}
              onRaceFieldModify={handleRaceFieldModify}
              disabled={isReadOnly || !allPending || isPending || isEventDead}
              isBlockValidated={isBlockValidated('races')}
              onValidateBlock={isReadOnly ? undefined : () => validateBlock('races')}
              onUnvalidateBlock={isReadOnly ? undefined : () => unvalidateBlock('races')}
              isBlockPending={isBlockPending}
              validationDisabled={isReadOnly || isEventDead}
              showCurrentValue={false}
              showActions={false}
              showDeleteAction={false}
            />

            <ProposalJustificationsCard
              justifications={proposal.justification || []}
              confidence={proposal.confidence}
            />
          </>
        )
      }}
      renderSidebar={(context) => {
        const {
          proposal,
          getEditionYear,
          selectedChanges,
          userModifiedChanges
        } = context

        // Extraire les matches rejetés depuis les justifications
        // Contrat: type === 'rejected_matches' (obligatoire)
        // Fallback: type === 'text' pour rétro-compatibilité avec anciennes propositions
        const rejectedMatches = proposal.justification
          ?.find((j: any) => j.type === 'rejected_matches' || j.type === 'text')
          ?.metadata?.rejectedMatches || []

        return (
          <>
            {proposal && (
              <EditionContextInfo
                currentCalendarStatus={
                  userModifiedChanges['calendarStatus'] ||
                  selectedChanges['calendarStatus'] ||
                  (typeof proposal.changes.calendarStatus === 'string'
                    ? proposal.changes.calendarStatus
                    : (proposal.changes.calendarStatus as any)?.current || (proposal.changes.calendarStatus as any)?.proposed)
                }
                currentEditionYear={getEditionYear(proposal) ? parseInt(getEditionYear(proposal)!) : undefined}
                previousEditionYear={(proposal as any).previousEditionYear}
                previousCalendarStatus={(proposal as any).previousEditionCalendarStatus}
                previousEditionStartDate={(proposal as any).previousEditionStartDate}
                eventName={(proposal as any).eventName}
                eventSlug={(proposal as any).eventSlug}
              />
            )}

            <AgentCard
              agent={{
                name: proposal.agent.name,
                type: proposal.agent.type
              }}
              createdAt={proposal.createdAt}
            />

            {/* Afficher la card des matches rejetés */}
            {rejectedMatches.length > 0 && (
              <RejectedMatchesCard
                proposalId={proposal.id}
                rejectedMatches={rejectedMatches}
                proposalYear={
                  proposal.changes?.edition?.new?.year
                    ? parseInt(proposal.changes.edition.new.year)
                    : proposal.editionYear || new Date().getFullYear()
                }
              />
            )}
          </>
        )
      }}
    />
  )
}

export default NewEventDetail
